import { useCallback, useRef, useState } from "react";

import { useAvisoSalida } from "../../lib/useAvisoSalida";
import { ConfirmDialog } from "./ConfirmDialog";

/**
 * Protege un formulario en modal de perder lo escrito (auditoría UX-01).
 *
 * `cerrar` reemplaza al `onClose` del modal y al botón Cancelar: sin cambios
 * cierra directo; con cambios pregunta "Seguir editando" / "Descartar
 * cambios". Mientras se guarda (`guardando`) no cierra. Con cambios, el
 * navegador además pregunta antes de recargar o cerrar la pestaña.
 *
 * Al guardar con éxito, el caller cierra con su `onClose` original (no con
 * `cerrar`), así que no aparece el aviso de descarte.
 *
 * Uso:
 *   const { cerrar, dialogo } = useDescarteProtegido(hayCambios, onClose, isPending);
 *   <Modal onClose={cerrar} footer={<Button onClick={cerrar}>Cancelar</Button>}>
 *     …
 *     {dialogo}
 *   </Modal>
 */
/**
 * ¿El formulario cambió desde que se abrió? Compara `valores` con la foto que
 * se toma la primera vez que `listo` es true. Pasar `listo=false` mientras
 * el formulario se completa solo (un socio que llega por la URL, un default
 * que sale de una query), para que eso no cuente como cambio del operador.
 */
export function useHayCambios(valores: readonly unknown[], listo = true): boolean {
  const actual = JSON.stringify(valores);
  const inicial = useRef<string | null>(null);
  if (inicial.current === null && listo) inicial.current = actual;
  return inicial.current !== null && actual !== inicial.current;
}

export function useDescarteProtegido(
  hayCambios: boolean,
  onClose: () => void,
  guardando = false,
): { cerrar: () => void; dialogo: JSX.Element } {
  const [preguntando, setPreguntando] = useState(false);
  useAvisoSalida(hayCambios);

  const cerrar = useCallback(() => {
    if (guardando) return;
    if (hayCambios) setPreguntando(true);
    else onClose();
  }, [guardando, hayCambios, onClose]);

  const dialogo = (
    <ConfirmDialog
      open={preguntando}
      title="¿Descartar los cambios?"
      description="Lo que escribiste en este formulario todavía no se ha guardado."
      cancelLabel="Seguir editando"
      confirmLabel="Descartar cambios"
      onCancel={() => setPreguntando(false)}
      onConfirm={() => {
        setPreguntando(false);
        onClose();
      }}
    />
  );

  return { cerrar, dialogo };
}
